import React, {
  createContext,
  useMemo,
  useRef,
  useContext,
  useState,
} from "react";
import FormComponent from "./FormComponent";

// https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_get
const get = (obj, path, defaultValue = undefined) => {
  const travel = (regexp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce(
        (res, key) => (res !== null && res !== undefined ? res[key] : res),
        obj
      );
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === obj ? defaultValue : result;
};

/**
 * @callback GetRefCallback
 * @returns {import('react').MutableRefObject<Object>}
 */

/**
 * @typedef {Object} FormContextValue
 * @property {GetRefCallback} getRef
 * @property {Object} data
 */

/** @type {import('react').Context<FormContextValue>} */
const FormContext = createContext(null);

/**
 * @typedef {Object} FormProps
 * @property {Function} [setRef]
 * @property {Function} [onChange]
 * @property {Function} [onSubmit]
 * @property {Function} [onValidSubmit]
 * @property {import('react').ReactNode} children
 */

/** @type {import('react').FunctionComponent<FormProps>} */
const Form = ({ setRef, ...props }) => {
  const formRef = useRef();
  const [data, updateData] = useState();

  const contextValue = useMemo(() => {
    return {
      getRef: () => formRef,
      data,
    };
  }, [data]);

  return (
    <FormContext.Provider value={contextValue}>
      <FormComponent
        {...props}
        onChange={(data, ...args) => {
          updateData(data);
          if (props.onChange) {
            props.onChange(data, ...args);
          }
        }}
        ref={(node) => {
          formRef.current = node;
          setRef && setRef(node);
        }}
      />
    </FormContext.Provider>
  );
};

export const useFormRef = () => {
  const context = useContext(FormContext);
  return context.getRef();
};

export const useFormData = (name) => {
  const context = useContext(FormContext);
  return context.data && name ? get(context.data, name) : context.data;
};

export const withFormData = (mapPropsToName) => (BaseComponent) => {
  const ComponentWithFormData = (props) => {
    const data = useFormData(mapPropsToName(props));
    return <BaseComponent {...props} formData={data} />;
  };
  return ComponentWithFormData;
};

export default Form;
